import { paramCase } from './changeCase';
import { isNullOrUndefined, isNullOrEmpty, isObject } from './typeChecks';
import { IComponentStyle } from '../models/componentStyle/IComponentStyle';
import { IBreakpoint } from '../models/componentStyle/IBreakpoint';
import { ISelectors } from '../models/componentStyle/ISelectors';
import { EOL } from 'os';
import { IConditionalStyle } from '../models/componentStyle/IConditionalStyle';
import { ConditionalHelper } from '../helpers/ConditionalHelper';

export function getComponentCss(cssSelector: string, componentStyle: IComponentStyle | undefined | null, props?: any, existingStyles?: string[]) {
	const response: string[] = existingStyles || [];

	if (componentStyle) {
		const {
			conditionals,
		} = componentStyle;

		addStylesForConditionals(cssSelector, response, conditionals, props);
	}
	if (props && props.componentStyle) {
		getCss(cssSelector, props.componentStyle, props, response);
	}

	return response;
}

export function getCss(cssSelector: string, componentStyle: IComponentStyle | undefined | null, props?: any, existingStyles?: string[], prefix: string = '') {
	const response: string[] = existingStyles || [];

	if (componentStyle) {
		const {
			breakpoints,
			selectors,
			conditionals,
		} = componentStyle;
		const exclusionKeys = ['_raw', 'breakpoints', 'selectors', 'conditionals', '_parameters', 'sizes', 'priorities', 'children', 'show', 'overrides', 'arrowDirection', 'chartProps', 'seriesColors', 'selected', 'groupHeader', 'groupChild'];
		const innerCss: string[] = [];
		// Loop over the available keys in the theme element object and add the styles for those to the response
		Object.entries(componentStyle).map(([key, value]) => {
			if (exclusionKeys.indexOf(key) === -1) {
				addStyleForObjectOrString(innerCss, value, paramCase(key));
			}
		});
		if (componentStyle._raw && !isNullOrEmpty(componentStyle._raw)) {
			innerCss.push(componentStyle._raw);
		}

		const cleanCss = getCleanStyle(innerCss);
		if (cleanCss && cleanCss.length > 0) {
			response.push(`${prefix}${cssSelector} {`);
			response.push(`${prefix}	${cleanCss.join(EOL + '\t')}`);
			response.push(`${prefix}}`);
		}

		addStylesForConditionals(cssSelector, response, conditionals, props);

		// Break points happen at the end for any overrides
		addStyleForBreakpoints(cssSelector, response, breakpoints, props);

		// ISelectors (hover etc)
		addStyleForSelectors(cssSelector, response, selectors, props);

		// Overrides for child components
		addStyleOverrides(cssSelector, response, componentStyle, props);
	}
	return getCleanStyle(response).join(EOL);
}


export function getCleanStyle(collection: string[]) {
	return collection.filter(value => !isNullOrEmpty(value));
}

export function addStyleForBreakpoints(cssSelector: string, collection: string[], breakpoints?: IBreakpoint[], props?: any) {
	if (breakpoints) {
		for (const breakpoint of breakpoints) {
			collection.push(...getStyleForBreakpoint(cssSelector, breakpoint, props));
		}
	}
}

export function getStyleForBreakpoint(cssSelector: string, breakpoint: IBreakpoint, props?: any) {
	const response: string[] = [];
	const key = !isNullOrUndefined(breakpoint.max) ? 'max' : (!isNullOrUndefined(breakpoint.min) ? 'min' : null);
	if (key) {
		const value = breakpoint[key];
		const defaultMedia = ['print', 'screen'];
		const media = breakpoint.media || defaultMedia;
		response.push(`@media ${media.join(', ')} and (${key}-width: ${value}) {`);
		getCss(cssSelector, breakpoint.style, props, response, '\t');
		response.push(`}`);
	}
	return response;
}

export function addStyleForSelectors(cssSelector: string, collection: string[], selectors?: ISelectors | ISelectors[], props?: any) {
	const selectorsArray: ISelectors[] = [];
	if (!isNullOrUndefined(selectors)) {
		if (Array.isArray(selectors)) {
			selectorsArray.push(...selectors);
		}
		else {
			selectorsArray.push(selectors);
		}
	}

	for (const selector of selectorsArray) {
		Object.entries(selector).map(([key, value]) => {
			let workingKey = key;
			if (value['_parameters'] && Array.isArray(value['_parameters'])) {
				workingKey += `(${value['_parameters'].join(',')})`;
			}
			const workingCssSelector = `${cssSelector}${workingKey}`;
			getCss(workingCssSelector, value as IComponentStyle, props, collection);
		});
	}
}

function addStylesForConditionals(cssSelector: string, collection: string[], conditionalStyles: IConditionalStyle[] | undefined, props: any) {
	const conditionalCss = (conditionalStyles || []).reduce((acc: string[], conditionalStyle) => {
		if (conditionalStyle) {
			const { conditional, style } = conditionalStyle;
			const conditionalHelper = new ConditionalHelper(conditional);
			if (conditionalHelper.matches(props)) {
				acc.push(getCss(cssSelector, style, props));
			}
		}
		return acc;
	}, []);
	return collection.push(...conditionalCss);
}

export function addStyleOverrides(cssSelector: string, collection: string[], componentStyle: IComponentStyle | undefined | null, props?: any) {
	if (componentStyle && componentStyle.overrides) {
		for (const override of componentStyle.overrides) {
			const { selector, style } = override;
			const childSelector = [cssSelector, selector].join(' ');
			getCss(childSelector, style, props, collection);
		}
	}
}

export function getStyleForObject(styleObject: any, prefix: string) {
	const workingPrefix = paramCase(prefix);
	const response: string[] = [];
	if (!isNullOrUndefined(styleObject)) {
		if (!isNullOrUndefined(styleObject['_value'])) {
			response.push(`${workingPrefix}: ${styleObject['_value']};`);
		}
		Object.entries(styleObject).map(([key, value]) => {
			if (!key.startsWith('_') && !isNullOrUndefined(value) && value !== '') {
				if (isObject(value)) {
					response.push(...getStyleForObject(value, `${workingPrefix}-${paramCase(key)}`));
				}
				else {
					response.push(`${workingPrefix}-${paramCase(key)}: ${value};`);
				}
			}
		});
	}
	return response;
}

export function addStyleForObjectOrString(collection: string[], styleObject: any, prefix: string) {
	const workingPrefix = paramCase(prefix);
	const response: string[] = [];
	if (!isNullOrUndefined(styleObject)) {
		if (isObject(styleObject)) {
			response.push(...getStyleForObject(styleObject, workingPrefix));
		}
		else {
			response.push(`${workingPrefix}: ${styleObject};`);
		}
		collection.push(...response);
	}
	return '';
}
